import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react";

import { useAuth0 } from "@auth0/auth0-react";

const APITokenContext = createContext<any>("apiToken");
APITokenContext.displayName = "APITokenContext";

type TokenState = {
  status: string | null;
  events: Record<string, GetTokenOptions[]>;
  tokens: Record<string, APIToken>;
};

type GetTokenOptions = {
  audience: string;
  scope: string;
};

type APIToken = {
  name: string;
  audience: string;
  scope: string;
  accessToken: string | null;
  status: string | null;
  error: string | null;
  renewToken: string | null;
};

const initialState: TokenState = {
  status: null,
  events: {},
  tokens: {},
};

function apiTokenReducer(state: any, action: any) {
  switch (action.type) {
    case "start getting new api": {
      return {
        ...state,
        status: "pending",
        events: {
          ...state.events,
          [action.token.name]: { ...action.event },
        },
        tokens: {
          ...state.tokens,
          [action.token.name]: { ...action.token },
        },
        error: null,
      };
    }
    case "finish adding new access token": {
      return {
        ...state,
        status: "resolved",
        events: action.events,
        tokens: {
          ...state.tokens,
          [action.token.name]: { ...action.token },
        },
        error: null,
      };
    }
    case "start update": {
      return {
        ...state,
        status: "pending",
        error: null,
      };
    }
    case "finish update": {
      return {
        ...state,
        status: "resolved",
        error: null,
        token: action.token,
      };
    }
    case "fail update": {
      return {
        ...state,
        status: "rejected",
        error: action.error,
      };
    }
    case "reset": {
      return {
        status: null,
        error: null,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function APITokenProvider({ children }: any) {
  const [state, dispatch] = useReducer(apiTokenReducer, initialState);

  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();

  const enqueueAPIToken = (options: GetTokenOptions) => {
    const { audience, scope } = options;

    dispatch({
      type: "start getting new api",
      event: options,
      token: {
        name: audience,
        audience,
        scope,
        accessToken: null,
        status: "pending",
        error: null,
      },
    });
  };

  const requestAccessToken = useCallback(
    (options: GetTokenOptions) => {
      const { tokens, events } = state;

      const { audience } = options;
      if (!tokens[audience] && !events[audience]) {
        enqueueAPIToken(options);
      }
    },
    [state]
  );

  const getToken = useCallback(
    async (options) => {
      try {
        return await getAccessTokenSilently(options);
      } catch (e) {
        try {
          return await getAccessTokenWithPopup(options);
        } catch (e: unknown) {
          if (e instanceof Error) {
            throw e;
          }
        }
      }
    },
    [getAccessTokenSilently, getAccessTokenWithPopup]
  );

  const renewToken = useCallback(
    async (options) => {
      dispatch({
        type: "start update",
      });

      try {
        const token: any = await getToken(options);
        dispatch({
          type: "finish update",
          token,
        });
        return token;
      } catch (e) {
        if (e instanceof Error) {
          dispatch({
            type: "fail update",
            error: e,
          });
        }
      }
    },
    [getToken]
  );

  const loop = useCallback(async () => {
    const temp = { ...state.events };
    const tempEvents = Object.entries(state.events).map(([, v]) => ({
      ...(v as any),
    }));

    for (const event of tempEvents) {
      const { audience, scope } = event;
      let accessToken = "";

      let error = null;
      try {
        accessToken = (await getToken(event)) as any;
      } catch (e) {
        error = e;
      }

      delete temp[event.audience];

      dispatch({
        type: "finish adding new access token",
        events: temp,
        token: {
          accessToken,
          name: audience,
          audience,
          scope,
          status: "resolved",
          error,
        },
      });
    }
  }, [state.events, getToken]);

  useEffect(() => {
    if (Object.keys(state.events).length) {
      loop();
    }
  }, [state.events, state.status, loop]);

  const value = {
    state: { ...state, requestAccessToken, renewToken },
    dispatch,
  };

  return (
    <APITokenContext.Provider value={value}>
      {children}
    </APITokenContext.Provider>
  );
}

function useAPIToken() {
  const context = useContext(APITokenContext);
  if (context === undefined) {
    throw new Error(`useAPIToken must be used within a APITokenProvider`);
  }
  return context;
}

export { APITokenProvider, useAPIToken };
